import {
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges
} from '@angular/core';
import { Router } from '@angular/router';
import { GUID_EMPTY } from '@core';
import uniqueId from 'lodash/uniqueId';
import { NzModalService } from 'ng-zorro-antd/modal';
import { Subscription } from 'rxjs';
import { ReceivePaymentService } from 'src/app/routes/ar/receive-payment/aror.service';
import { DateFormatService } from 'src/app/shared/services/date-format.service';

import { BankChargesComponent } from '../bank-charges/bank-charges.component';

@Component({
  standalone: false,
  selector: 'app-deposit-entry',
  templateUrl: './deposit.component.html',
  styleUrls: ['./deposit.component.less']
})
export class DepositComponent implements OnChanges, OnInit, OnDestroy {
  @Input() depositGroupId: string | null = null;
  @Input() editable: boolean = false;

  currentId: string | null = null;
  loading: boolean = false;
  records: any[] = [];
  modalVisible: {
    listing: boolean;
    entry: boolean;
  } = { listing: false, entry: false };

  private accountValueChangesSub?: Subscription;

  get depositForm() {
    return this.arorService.form;
  }

  constructor(
    private modalSrv: NzModalService,
    private arorService: ReceivePaymentService,
    public dateFormatService: DateFormatService,
    private cdr: ChangeDetectorRef,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.accountValueChangesSub =
      this.depositForm.controls.account.valueChanges.subscribe(val =>
        this.onDepositAccountChanged(val)
      );
  }

  ngOnDestroy(): void {
    this.accountValueChangesSub?.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['editable']) {
      if (changes['editable'].currentValue) {
        this.depositForm.enable();
      } else {
        this.depositForm.disable();
      }
    }

    if (changes['depositGroupId']) {
      this.records = [];
      this.reload();
    }
  }

  getDeposits() {
    return this.records;
  }

  getTotalAmount() {
    return this.records.reduce((total, record) => {
      return total + (record.totalAmount || 0);
    }, 0);
  }

  openListing() {
    this.modalVisible.listing = true;
    this.reload();
  }

  openDetail(record: any = null) {
    // Deposit will only have docCode after save, so if docCode is not present, it means the record is not saved yet.
    if (record) {
      this.currentId = record.id || record.tempId;
      this.depositForm.patchValue({ ...record });
      this.syncDepositAccountId();
      this.modalVisible.entry = true;
      this.cdr.detectChanges();
    } else {
      this.arorService.new().subscribe((res: any) => {
        this.depositForm.patchValue({ ...res });
        this.syncDepositAccountId();
        this.currentId = null;
        this.modalVisible.entry = true;
        this.cdr.detectChanges();
      });
    }
  }

  reload() {
    if (this.depositGroupId && this.records.length === 0) {
      this.loading = true;
      this.arorService.getDepositRecords(this.depositGroupId).subscribe({
        next: (res: any) => {
          this.records = res;
          this.loading = false;
          this.cdr.detectChanges();
        }
      });
    }
  }

  bankChargesModal() {
    const modal = this.modalSrv.create({
      nzTitle: 'Bank Charges',
      nzContent: BankChargesComponent,
      nzWidth: '600px',
      nzOnOk: (component: any) => {
        const instance = modal.getContentComponent();
        this.depositForm.controls['bankChargesAccountId'].setValue(
          instance.bankChargesAccountId
        );
      }
    });

    modal.afterOpen.subscribe((res: any) => {
      const instance = modal.getContentComponent();
      instance.bankChargesAccountId =
        this.depositForm.controls['bankChargesAccountId'].value;
    });
  }

  private hasDepositAccount(accountValue: any): boolean {
    return !!accountValue?.id && accountValue.id !== GUID_EMPTY;
  }

  /** Sync accountId when deposit modal is used outside AROR (observeChanges is false). */
  private onDepositAccountChanged(accountValue: any): void {
    if (!this.modalVisible.entry || !this.hasDepositAccount(accountValue)) {
      return;
    }

    const account = this.depositForm.controls.account;
    const accountId = this.depositForm.controls.accountId;

    if (account.hasError('required')) {
      account.setErrors(null);
    }
    if (!accountId.value) {
      accountId.setValue(accountValue.id, { emitEvent: false });
    }
    this.cdr.detectChanges();
  }

  private syncDepositAccountId(): void {
    const accountValue = this.depositForm.controls.account.value;
    if (!this.hasDepositAccount(accountValue)) {
      return;
    }
    const accountId = this.depositForm.controls.accountId;
    if (!accountId.value) {
      accountId.setValue(accountValue.id, { emitEvent: false });
    }
  }

  addDeposit(): boolean {
    const account = this.depositForm.controls.account;
    const accountValue = account.value;

    if (!this.hasDepositAccount(accountValue)) {
      account.markAsTouched();
      account.setErrors({ required: true });
      this.cdr.detectChanges();
      return false;
    }

    if (account.hasError('required')) {
      account.setErrors(null);
    }
    this.syncDepositAccountId();

    var formValue: any = {
      ...this.depositForm.value,
      docType: 'AROR'
    };

    // Update the record with the current ID if it exists
    if (this.currentId) {
      const index = this.records.findIndex(
        record =>
          record.id === this.currentId || record.tempId === this.currentId
      );
      if (index > -1) {
        this.records[index] = { ...this.records[index], ...formValue }; // Update existing record
      }
    } else {
      // If no current ID, create a temporary id
      formValue = { ...formValue, tempId: uniqueId() };
      this.records.push(formValue);
    }

    this.currentId = null; // Reset current ID after saving
    this.modalVisible.entry = false;
    this.depositForm.reset();
    return true;
  }

  removeDeposit(id: any) {
    this.records = this.records.filter(record => record.tempId !== id);
  }

  navigateToDeposit(id: any) {
    this.modalVisible.listing = false;
    this.cdr.detectChanges();
    this.router.navigateByUrl(`/ar/or/detail/${id}`);
  }
}
